import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Pagination control — Previous / Next + numbered pages
 * Shows up to 7 page buttons with ellipses for long ranges.
 */
export default function Pagination({ page, totalPages, onPageChange }) {
  if (!totalPages || totalPages <= 1) return null;

  const pages = buildPageList(page, totalPages);
  const go = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    onPageChange(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <nav
      className="flex items-center justify-center gap-1 mt-10 select-none"
      aria-label="Pagination"
      data-testid="pagination"
    >
      <button
        onClick={() => go(page - 1)}
        disabled={page <= 1}
        className="h-10 px-3 rounded-xl border border-stone-200 bg-white hover:bg-stone-50 disabled:opacity-40 inline-flex items-center gap-1 text-sm font-medium"
        data-testid="pagination-prev"
      >
        <ChevronLeft className="w-4 h-4" /> <span className="hidden sm:inline">Précédent</span>
      </button>

      {pages.map((p, i) =>
        p === "..." ? (
          <span key={`e${i}`} className="px-2 text-stone-400">…</span>
        ) : (
          <button
            key={p}
            onClick={() => go(p)}
            className={`h-10 min-w-[2.5rem] px-2 rounded-xl text-sm font-semibold border ${
              p === page
                ? "bg-lokaso-green text-white border-lokaso-green"
                : "bg-white border-stone-200 hover:bg-stone-50 text-stone-700"
            }`}
            data-testid={`pagination-page-${p}`}
            aria-current={p === page ? "page" : undefined}
          >
            {p}
          </button>
        )
      )}

      <button
        onClick={() => go(page + 1)}
        disabled={page >= totalPages}
        className="h-10 px-3 rounded-xl border border-stone-200 bg-white hover:bg-stone-50 disabled:opacity-40 inline-flex items-center gap-1 text-sm font-medium"
        data-testid="pagination-next"
      >
        <span className="hidden sm:inline">Suivant</span> <ChevronRight className="w-4 h-4" />
      </button>
    </nav>
  );
}

function buildPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, total, current, current - 1, current + 1]);
  if (current <= 4) [2, 3, 4, 5].forEach((p) => pages.add(p));
  if (current >= total - 3) [total - 4, total - 3, total - 2, total - 1].forEach((p) => pages.add(p));
  const sorted = Array.from(pages).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    out.push(sorted[i]);
    if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) out.push("...");
  }
  return out;
}
